<!-- A live interactive HTML preview for a current menu with multiple pages

Has hover buttons for previous/next page as well as current position indicators.

-->
<template>
  <v-layout :justify-center="center">
    <v-hover v-if="menuPage">
      <div
        :key="menuPages.length"
        slot-scope="{hover}"
        style="position: relative"
      >
        <tv-template-preview
          ref="current-menu-preview"
          :busy="busy"
          :fake-player-progress="showProgressbar"
          :progressbar-color="menuPage.menu_template.progressbar_color"
          :progressbar-height="
            (menuPage.menu_template.progressbar_height / 3) * progressbarScale
          "
          :html-overlay="menuPage.menu_template.html_overlay"
          :width="tvdecoder.tvformat.width"
          :height="tvdecoder.tvformat.height"
          :max-width="previewMaxWidth"
          :max-height="previewMaxHeight"
          :duration="menuPage.menu_template.duration"
          :hide-restart-button="true"
          :class="[elevationClass]"
          :onload-callback="
            () => populateCurrentMenuPagePreviewIframeWithOverlayData(menuPage)
          "
          @player-frame-wrapped="nextPage()"
        />
        <!-- Previous Page Button -->
        <v-slide-x-reverse-transition>
          <div
            v-show="
              (forceShowButtons || !hideButtons) &&
                hover &&
                menuPages.length > 1
            "
            :title="$t('menuPreviewGoToPreviousPage')"
            class="unselectable clickable preview-nav-button"
            style="left: 0"
            @click="previousPage()"
          >
            <v-icon
              color="white"
              style="opacity: 0.85"
              :style="{'font-size': previewMaxWidth / 60 + 'em'}"
            >
              arrow_left
            </v-icon>
          </div>
        </v-slide-x-reverse-transition>
        <!-- Page/position indicator dots -->
        <v-slide-y-reverse-transition>
          <v-layout
            v-show="(forceShowButtons || !hideButtons) && menuPages.length > 1"
            style="
              position: absolute;
              bottom: 1%;
              width: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
            "
          >
            <div
              v-for="(page, pageIndex) in menuPages"
              :key="pageIndex"
              class="clickable"
              :title="
                $t('menuPreviewGoToIndexedPage', {
                  index: pageIndex + 1,
                  length: menuPages.length,
                })
              "
              style="
                width: 9px;
                height: 9px;
                border-radius: 50%;
                background: white;
                margin: 1.5%;
              "
              :style="{opacity: pageIndex + 1 == paginationIndex ? 1.0 : 0.5}"
              @click="goToPageByIndex(pageIndex + 1)"
            />
          </v-layout>
        </v-slide-y-reverse-transition>
        <!-- Next Page Button -->
        <v-slide-x-transition>
          <div
            v-show="
              (forceShowButtons || !hideButtons) &&
                hover &&
                menuPages.length > 1
            "
            :title="$t('menuPreviewGoToNextPage')"
            class="unselectable clickable preview-nav-button"
            style="right: 0"
            @click="nextPage()"
          >
            <v-icon
              color="white"
              style="opacity: 0.85"
              :style="{'font-size': previewMaxWidth / 60 + 'em'}"
            >
              arrow_right
            </v-icon>
          </div>
        </v-slide-x-transition>
      </div>
    </v-hover>
    <v-layout
      v-else
      class="
        pa-3
        subheading
        font-weight-bold
        text-xs-center
        warning--text
        grey
        darken-4
      "
      :class="[elevationClass]"
      style="background: rgba(0, 0, 0, 0.08)"
      :style="{
        'max-height': noPreviewMaxHeightPixel,
        'max-width': noPreviewMaxWidthPixel,
        'min-height': noPreviewMaxHeightPixel,
        'min-width': noPreviewMaxWidthPixel,
      }"
      column
      align-center
      justify-center
    >
      <v-icon
        color="warning"
        x-large
        class="mb-2"
      >
        {{ noPreviewWarningIcon }}
      </v-icon>
      {{ $t(noPreviewWarning) }}
    </v-layout>
  </v-layout>
</template>

<script>
import TvTemplatePreview from '@/components/TvTemplatePreview';

export default {
  components: {
    TvTemplatePreview,
  },
  props: {
    tvdecoder: {
      type: Object,
      required: true,
    },
    hideButtons: {
      type: Boolean,
      default: false,
    },
    forceShowButtons: {
      type: Boolean,
      default: false,
    },
    previewMaxWidth: {
      type: Number,
      default: 560,
    },
    previewMaxHeight: {
      type: Number,
      default: 560,
    },
    elevationClass: {
      type: String,
      default: 'elevation-3',
    },
    center: {
      type: Boolean,
      default: true,
    },
    noPreviewWarning: {
      type: String,
      default: () => {
        return 'tvdecoderMenuPreviewNotPossible';
      },
    },
    noPreviewWarningIcon: {
      type: String,
      default: () => {
        return 'warning';
      },
    },
    busy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // From 1 to MenuPages.length
      paginationIndex: 1,
    };
  },
  computed: {
    tvformatAspectRatio() {
      return this.tvdecoder.tvformat.aspect_ratio;
    },
    noPreviewMaxWidthPixel() {
      const buffer = 32;

      let maxWidth = this.previewMaxWidth;
      if (this.$vuetify.breakpoint.width - buffer < maxWidth) {
        maxWidth = this.$vuetify.breakpoint.width - buffer;
      }

      if (this.tvformatAspectRatio < 1) {
        return maxWidth * this.tvformatAspectRatio + 'px';
      }
      return maxWidth + 'px';
    },
    noPreviewMaxHeightPixel() {
      const buffer = 32;

      let maxHeight = this.previewMaxHeight;
      if (this.$vuetify.breakpoint.height - buffer < maxHeight) {
        maxHeight = this.$vuetify.breakpoint.height - buffer;
      }

      if (this.tvformatAspectRatio > 1) {
        return maxHeight / this.tvformatAspectRatio + 'px';
      }
      return maxHeight + 'px';
    },
    showProgressbar() {
      return this.menuPages.length > 1;
    },
    progressbarScale() {
      // 640 px max-width was sort of the baseline, use it as a scaling
      // reference to not have giant progressbars on tiny previews.
      return this.previewMaxWidth / 640;
    },
    // From 0 to MenuPages.length - 1
    menuPageIndex() {
      return this.paginationIndex - 1;
    },
    currentMenu() {
      if (!this.tvdecoder) {
        return null;
      }
      return this.tvdecoder.current_menu;
    },
    /**
     * We use this as :key to detect changes to the menu and update the preview.
     */
    currentMenuAsString() {
      return JSON.stringify(this.currentMenu || {});
    },
    menuPages() {
      if (!this.currentMenu) {
        return [];
      }
      return this.currentMenu.menu_pages;
    },
    menuPage() {
      return this.menuPages[this.menuPageIndex];
    },
  },
  watch: {
    /**
     * If the menu changes, update the preview without abrupt flickering.
     *
     * A :key would cause a more disruptive preview as the iframe loads
     * new from scratch, this update here is more quiet.
     */
    currentMenuAsString: function () {
      let matched = false;
      for (
        let menuPageIndex = 0;
        menuPageIndex < this.menuPages.length;
        menuPageIndex++
      ) {
        if (menuPageIndex === this.menuPageIndex) {
          this.displayMenuPageByIndex(menuPageIndex);
          matched = true;
          break;
        }
      }
      if (!matched) {
        this.displayMenuPageByIndex(0);
      }
    },
    /**
     * Fix index if a change leads us from multi-page to single-page.
     */
    'menuPages.length': function () {
      if (this.menuPages.length == 1) {
        this.paginationIndex = 1;
      }
    },
    menuPageIndex: function (menuPageIndex) {
      this.displayMenuPageByIndex(menuPageIndex);
    },
  },
  methods: {
    goToPageByIndex(paginationIndex) {
      // Note: The actual pagination index is 1-based, not 0-based!
      this.paginationIndex = paginationIndex;
    },
    previousPage() {
      this.paginationIndex -= 1;
      if (this.paginationIndex < 1) {
        this.paginationIndex = this.menuPages.length;
      }
    },
    nextPage() {
      this.paginationIndex += 1;
      if (this.paginationIndex > this.menuPages.length) {
        this.paginationIndex = 1;
      }
    },
    displayMenuPageByIndex(menuPageIndex) {
      const menuPage = this.menuPages[menuPageIndex];
      this.populateCurrentMenuPagePreviewIframeWithOverlayData(menuPage);
    },
    populateCurrentMenuPagePreviewIframeWithOverlayData(
      menuPage,
      restartPlayer = true
    ) {
      const overlayData = {menu_page: menuPage};
      const previewWidget = this.$refs['current-menu-preview'];
      if (!previewWidget) {
        return;
      }
      previewWidget.updateOverlayData(overlayData);
      if (restartPlayer) {
        previewWidget.restartPlayer();
      }
    },
  },
};
</script>

<style scoped>
.preview-nav-button {
  position: absolute;
  bottom: 0;
  height: 30%;
  width: 15%;
  background: rgba(0, 0, 0, 0.066);
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>



// WEBPACK FOOTER //
// src/components/DecoderDetail/TvDecoderMenuPreview.vue